import React, { useState, useEffect } from 'react';
import { updateDonorProfile, useDonationHistory } from '../hooks/useSupabase';
import { DISTRICTS_KERALA, BLOOD_GROUPS, PARISHES_KERALA, isDonorEligible } from '../types/schema';
import NotificationSettings from './NotificationSettings';
import DonationRecorder from './DonationRecorder';

const DonorProfile = ({ donor, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const achievements = donor?.achievements || [];
  const [formData, setFormData] = useState({
    name: donor?.name || '',
    email: donor?.email || '',
    phoneNumber: donor?.phoneNumber || '',
    location: donor?.location || '',
    parish: donor?.parish || '',
    isAvailable: donor?.isAvailable !== false,
    showEmail: donor?.showEmail || false,
    showFullName: donor?.showFullName !== false,
  });

  const { data: donationHistory, loading: historyLoading } = useDonationHistory(donor?.id);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateDonorProfile(donor.id, formData);
      setIsEditing(false);
      if (onUpdate) {
        onUpdate({ ...donor, ...formData });
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDonationUpdate = async (donationDate) => {
    setLoading(true);
    try {
      await updateDonorProfile(donor.id, {
        lastDonationDate: new Date(donationDate),
      });
      if (onUpdate) {
        const nextEligible = calculateNextEligibleDate(new Date(donationDate));
        onUpdate({ 
          ...donor, 
          lastDonationDate: new Date(donationDate),
          nextEligibleDate: nextEligible,
          totalDonations: (donor.totalDonations || 0) + 1
        });
      }
    } catch (error) {
      console.error('Failed to update donation date:', error);
      alert('Failed to update donation date. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isEligible = donor ? isDonorEligible(donor) : false;
  const nextEligibleDate = donor?.nextEligibleDate ? new Date(donor.nextEligibleDate) : null;
  const daysSinceLastDonation = donor?.lastDonationDate 
    ? Math.floor((new Date() - new Date(donor.lastDonationDate)) / (1000 * 60 * 60 * 24))
    : null;

  if (!donor) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <p className="text-gray-500">No donor profile found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Donor Profile</h2>
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Edit Profile
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: donor.name || '',
                    email: donor.email || '',
                    phoneNumber: donor.phoneNumber || '',
                    location: donor.location || '',
                    parish: donor.parish || '',
                    isAvailable: donor.isAvailable !== false,
                    showEmail: donor.showEmail || false,
                    showFullName: donor.showFullName !== false,
                  });
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Eligibility Status */}
      <div className={`mb-6 p-4 rounded-md ${isEligible ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-medium ${isEligible ? 'text-green-800' : 'text-yellow-800'}`}>
              Donation Status: {isEligible ? 'Eligible to Donate' : 'Not Eligible'}
            </h3>
            {!isEligible && nextEligibleDate && (
              <p className="text-yellow-700 text-sm mt-1">
                Next eligible date: {nextEligibleDate.toLocaleDateString()}
              </p>
            )}
            {daysSinceLastDonation !== null && (
              <p className="text-gray-600 text-sm mt-1">
                Days since last donation: {daysSinceLastDonation}
              </p>
            )}
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            donor.isAvailable 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {donor.isAvailable ? 'Available' : 'Unavailable'}
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-800 mb-4">Personal Information</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-gray-900">{donor.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-gray-900">{donor.email || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              {isEditing ? (
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-gray-900">{donor.phoneNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              {isEditing ? (
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-gray-900">{donor.location}, {donor.district}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Parish</label>
              {isEditing ? (
                <input
                  type="text"
                  name="parish"
                  value={formData.parish}
                  onChange={handleInputChange}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-gray-900">{donor.parish || 'Not specified'}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-800 mb-4">Medical Information</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Blood Group</label>
              <p className="mt-1 text-2xl font-bold text-red-600">{donor.bloodGroup}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Age</label>
              <p className="mt-1 text-gray-900">{donor.age} years</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Weight</label>
              <p className="mt-1 text-gray-900">{donor.weight} kg</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Total Donations</label>
              <p className="mt-1 text-gray-900">{donor.totalDonations || 0}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Last Donation</label>
              <p className="mt-1 text-gray-900">
                {donor.lastDonationDate 
                  ? new Date(donor.lastDonationDate).toLocaleDateString()
                  : 'Never donated'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      {isEditing && (
        <div className="mb-6 p-4 bg-gray-50 rounded-md">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Privacy & Availability Settings</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isAvailable"
                name="isAvailable"
                checked={formData.isAvailable}
                onChange={handleInputChange}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-700">
                Available for blood donation
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showEmail"
                name="showEmail"
                checked={formData.showEmail}
                onChange={handleInputChange}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="showEmail" className="ml-2 block text-sm text-gray-700">
                Show my email to blood requesters
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showFullName"
                name="showFullName"
                checked={formData.showFullName}
                onChange={handleInputChange}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label htmlFor="showFullName" className="ml-2 block text-sm text-gray-700">
                Show my full name
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Quick Donation Update */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <h3 className="text-blue-800 font-medium mb-2">Update Last Donation Date</h3>
        <p className="text-blue-700 text-sm mb-3">
          Did you donate blood recently? Update your last donation date to help us track your eligibility.
        </p>
        <div className="flex gap-2">
          <input
            type="date"
            max={new Date().toISOString().split('T')[0]}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => {
              if (e.target.value) {
                handleDonationUpdate(e.target.value);
              }
            }}
          />
          <span className="text-sm text-gray-500 self-center">Select donation date</span>
        </div>
      </div>

      {/* Donation History */}
      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-4">Donation History</h3>
        {historyLoading ? (
          <p className="text-gray-500">Loading donation history...</p>
        ) : donationHistory && donationHistory.length > 0 ? (
          <div className="space-y-3">
            {donationHistory.map((donation, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-md">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">
                      {donation.hospital?.name || 'Hospital not specified'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {donation.hospital?.location}
                    </p>
                    {donation.notes && (
                      <p className="text-sm text-gray-600 mt-1">{donation.notes}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800">
                      {new Date(donation.donationDate).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      {donation.units || 1} unit{(donation.units || 1) > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No donation history found.</p>
        )}
      </div>

      {/* Points and Achievements */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">🏆 Your Stats</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{donor?.points || 0}</div>
              <div className="text-sm text-gray-600">Points Earned</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{donor?.totalDonations || 0}</div>
              <div className="text-sm text-gray-600">Total Donations</div>
            </div>
          </div>
          {donor?.nextEligibleDate && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="text-sm text-yellow-800">
                <strong>Next eligible donation:</strong> {new Date(donor.nextEligibleDate).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">🏅 Achievements</h3>
          {achievements && achievements.length > 0 ? (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {achievements.map((achievement, index) => (
                <div key={index} className="flex items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border">
                  <div className="text-2xl mr-3">🏅</div>
                  <div className="flex-1">
                    <div className="font-semibold text-purple-800">{achievement.title}</div>
                    <div className="text-sm text-purple-600">{achievement.description}</div>
                    <div className="text-xs text-gray-500">
                      +{achievement.pointsAwarded} points • {new Date(achievement.unlockedAt?.toDate()).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="text-4xl mb-2">🎯</div>
              <p>No achievements yet!</p>
              <p className="text-sm">Start donating to unlock achievements and earn points!</p>
            </div>
          )}
        </div>
      </div>

      {/* Donation Recorder */}
      <div className="mt-8">
        <DonationRecorder 
          donor={donor} 
          onDonationRecorded={(result) => {
            // Refresh donor data after recording donation
            if (onUpdate) {
              onUpdate({ 
                ...donor, 
                points: (donor.points || 0) + result.pointsEarned,
                totalDonations: (donor.totalDonations || 0) + 1
              });
            }
          }}
        />
      </div>

      {/* Notification Settings */}
      <div className="mt-8">
        <NotificationSettings 
          donor={donor} 
          onUpdate={onUpdate}
        />
      </div>
    </div>
  );
};

export default DonorProfile;
